import {
  DEFAULT_COMPANY_ADMIN_ID,
  DEFAULT_TEAM_MANAGER_ID,
  DEFAULT_USER_ID,
  isNotEmpty,
  type PermissionEntityId,
} from '@terros/common'
import { type PermGraph, type PermGraphNode } from './types'
import { getPermGraphLevels, orderPermGraphLevelsByRelationships } from './buildPermGraph'

export type PermGraphDensity = 'dense' | 'linear' | 'sparse' | 'standard'

export type PermGraphLayoutMetrics = {
  columnGapClass: string
  density: PermGraphDensity
  nodeWidth: number
  rowGapClass: string
}

export type PermGraphSourceSide = 'left' | 'right'

export type PermGraphRoleSource = {
  node: PermGraphNode
  side: PermGraphSourceSide
}

export type PermGraphPathLayout = {
  backboneLevels: PermGraphNode[][]
  defaultNodes: PermGraphNode[]
  roleSourcesByTarget: Map<PermissionEntityId, PermGraphRoleSource[]>
}

const LAYOUTS: Record<PermGraphDensity, PermGraphLayoutMetrics> = {
  sparse: { columnGapClass: 'gap-x-4xl', density: 'sparse', nodeWidth: 380, rowGapClass: 'gap-y-5xl' },
  linear: { columnGapClass: 'gap-x-4xl', density: 'linear', nodeWidth: 360, rowGapClass: 'gap-y-5xl' },
  standard: { columnGapClass: 'gap-x-4xl', density: 'standard', nodeWidth: 340, rowGapClass: 'gap-y-5xl' },
  dense: { columnGapClass: 'gap-x-3xl', density: 'dense', nodeWidth: 320, rowGapClass: 'gap-y-4xl' },
}

export function getPermGraphLayoutMetrics(levelNodeCounts: number[]): PermGraphLayoutMetrics {
  const depth = levelNodeCounts.length
  const breadth = Math.max(0, ...levelNodeCounts)

  if (breadth === 1) return depth <= 5 ? LAYOUTS.sparse : LAYOUTS.linear
  if (breadth >= 4 || depth >= 7) return LAYOUTS.dense
  return LAYOUTS.standard
}

export function getPermGraphPathLayout(graph: PermGraph): PermGraphPathLayout {
  const nodes = new Map(graph.nodes.map((node) => [node.entityId, node]))
  const orderedNodes = getPermGraphLevels(graph).flat()
  const backboneDepths = getBackboneDepths(graph, nodes, orderedNodes)
  const levels = new Map<number, PermGraphNode[]>()
  orderedNodes.filter(isBackboneNode).forEach((node) => {
    const depth = backboneDepths.get(node.entityId) ?? 1
    levels.set(depth, [...(levels.get(depth) ?? []), node])
  })

  const backboneLevels = Array.from(levels.entries())
    .sort(([a], [b]) => a - b)
    .map(([, levelNodes]) => levelNodes)
  const orderedBackboneLevels = orderPermGraphLevelsByRelationships(backboneLevels, graph.edges)
  const fallbackTarget = orderedBackboneLevels.at(-1)?.at(0)
  const defaultNodes = orderedNodes
    .filter((node) => node.kind === 'defaultRole')
    .sort((a, b) => getDefaultLane(a.entityId) - getDefaultLane(b.entityId))
  const roleSourcesByTarget = new Map<PermissionEntityId, PermGraphRoleSource[]>()

  orderedNodes
    .filter((node) => node.kind === 'role' && !node.isTarget)
    .forEach((role) => {
      const target = graph.edges
        .filter((edge) => edge.source === role.entityId && edge.relation !== 'inherits')
        .map((edge) => nodes.get(edge.target))
        .filter((node): node is PermGraphNode => node !== undefined && isBackboneNode(node))
        .sort((a, b) => (backboneDepths.get(a.entityId) ?? Infinity) - (backboneDepths.get(b.entityId) ?? Infinity))[0]
      const anchor = target ?? fallbackTarget
      if (!anchor) return
      const currentSources = roleSourcesByTarget.get(anchor.entityId) ?? []
      roleSourcesByTarget.set(anchor.entityId, [
        ...currentSources,
        { node: role, side: getRoleSourceSide(role, graph, nodes, currentSources) },
      ])
    })

  return { backboneLevels: orderedBackboneLevels, defaultNodes, roleSourcesByTarget }
}

function getRoleSourceSide(
  role: PermGraphNode,
  graph: PermGraph,
  nodes: Map<PermissionEntityId, PermGraphNode>,
  currentSources: PermGraphRoleSource[]
): PermGraphSourceSide {
  const defaultAncestorId = findDefaultAncestorId(role.entityId, graph, nodes)
  if (defaultAncestorId === DEFAULT_TEAM_MANAGER_ID) return 'left'
  if (defaultAncestorId === DEFAULT_COMPANY_ADMIN_ID) return 'right'

  const leftCount = currentSources.filter((source) => source.side === 'left').length
  return leftCount <= currentSources.length - leftCount ? 'left' : 'right'
}

function findDefaultAncestorId(
  entityId: PermissionEntityId,
  graph: PermGraph,
  nodes: Map<PermissionEntityId, PermGraphNode>
): PermissionEntityId | undefined {
  const visited = new Set<PermissionEntityId>()
  const pending = [entityId]

  while (isNotEmpty(pending)) {
    const target = pending.shift()
    if (!target || visited.has(target)) continue
    visited.add(target)

    const parentIds = graph.edges
      .filter((edge) => edge.target === target && edge.relation === 'inherits')
      .map((edge) => edge.source)
    const defaultParent = parentIds.find((parentId) => nodes.get(parentId)?.kind === 'defaultRole')
    if (defaultParent) return defaultParent
    pending.push(...parentIds)
  }
}

function getDefaultLane(entityId: PermissionEntityId): number {
  if (entityId === DEFAULT_TEAM_MANAGER_ID) return 0
  if (entityId === DEFAULT_USER_ID) return 1
  if (entityId === DEFAULT_COMPANY_ADMIN_ID) return 2
  return 1
}

function getBackboneDepths(
  graph: PermGraph,
  nodes: Map<PermissionEntityId, PermGraphNode>,
  orderedNodes: PermGraphNode[]
): Map<PermissionEntityId, number> {
  const incoming = new Map<PermissionEntityId, PermissionEntityId[]>()
  graph.edges.forEach((edge) => {
    const source = nodes.get(edge.source)
    const target = nodes.get(edge.target)
    if (!source || !target || !isBackboneNode(source) || !isBackboneNode(target)) return
    incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge.source])
  })

  const depths = new Map<PermissionEntityId, number>()
  orderedNodes.forEach((node) => {
    if (!isBackboneNode(node)) return
    const minimumDepth = node.kind === 'company' ? 1 : 2
    const inheritedDepth = Math.max(
      0,
      ...(incoming.get(node.entityId) ?? []).map((entityId) => (depths.get(entityId) ?? 0) + 1)
    )
    depths.set(node.entityId, Math.max(minimumDepth, inheritedDepth))
  })
  return depths
}

function isBackboneNode(node: PermGraphNode): boolean {
  if (node.isTarget) return node.kind !== 'defaultRole'
  return node.kind === 'company' || node.kind === 'team' || node.kind === 'user'
}
